import { z } from 'zod';

import config from '@@config';
import { selectContentLocale, useSettingsStore } from '@@scripts/store/settings/useSettingsStore';
import { selectAuthToken, useAuthStore } from '@@scripts/store/useAuthStore';

import { CONTENT_LANGUAGE_HEADER } from './constants/headers';

const authHeaders = () => ({
    authorization: selectAuthToken(useAuthStore.getState()),
});

const AuthHeaders = z.object({
    authorization: z.string(),
});

const languageHeaders = () => {
    const settings = useSettingsStore.getState();

    return {
        'accept-language': settings.uiLocale,
        [CONTENT_LANGUAGE_HEADER]: selectContentLocale(settings),
    };
};

const LanguageHeaders = z.object({
    'accept-language': z.string(),
    [CONTENT_LANGUAGE_HEADER]: z.string().optional(),
});

const jsonHeaders = {
    accept: 'application/json',
    'content-type': 'application/json',
};

const JsonHeaders = z.object({
    accept: z.string(),
    'content-type': z.string(),
});

const patchJsonHeaders = {
    accept: 'application/json',
    'content-type': 'application/json-patch+json',
};

const PatchJsonHeaders = z.object({
    accept: z.string(),
    'content-type': z.string(),
});

const EtagHeaders = z.object({
    'if-match': z.string().optional(),
    etag: z.string().optional(),
});

export const fileUploadHeaders = () => ({
    ...authHeaders(),
    ...languageHeaders(),
    'content-type': 'multipart/form-data',
    accept: 'application/json',
});

export const FileUploadHeaders = z.object({
    ...AuthHeaders.shape,
    ...LanguageHeaders.shape,
    accept: z.literal('application/json'),
    'content-type': z.literal('multipart/form-data'),
});

export const loginHeaders = () => ({
    ...languageHeaders(),
    authorization: config.loginAuthHeader,
    accept: 'application/json',
    'content-type': 'application/x-www-form-urlencoded',
});

export const LoginHeaders = z.object({
    ...AuthHeaders.shape,
    ...LanguageHeaders.shape,
    accept: z.literal('application/json'),
    'content-type': z.literal('application/x-www-form-urlencoded'),
});

export const defaultHeaders = () => ({
    ...authHeaders(),
    ...languageHeaders(),
    ...jsonHeaders,
});

export const DefaultHeaders = z.object({
    ...AuthHeaders.shape,
    ...LanguageHeaders.shape,
    ...JsonHeaders.shape,
    ...EtagHeaders.shape,
});

export const patchDefaultHeaders = () => ({
    ...authHeaders(),
    ...languageHeaders(),
    ...patchJsonHeaders,
});

export const PatchDefaultHeaders = z.object({
    ...AuthHeaders.shape,
    ...LanguageHeaders.shape,
    ...PatchJsonHeaders.shape,
    ...EtagHeaders.shape,
});
